import { AxiosResponse } from 'axios';
import { SET_JOB_POSTS, SET_JOB_POST, SET_ERRORMSG } from '@/store/mutation_types';
import {
  CLEAR_JOB_POST_ISLOADING,
  DELETE_JOB_POST,
  EDIT_JOB_POST,
  GET_JOB_POSTS,
  GET_JOB_POST,
  REGISTER_JOB_POST,
  TOGGLE_JOB_POST_IS_ACTIVE,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import JobPost from '@/services/job_post';
import { Module } from 'vuex';
import { EditJobPost, RegisterJobPost, GetJobPost } from '@/dtos/job_post';

/** Replace (or drop) one post inside the cached list without refetching. */
const patchList = (list: any[] | null, id: string, patch: ((post: any) => any) | null): any[] | null => {
  if (!Array.isArray(list)) return list;
  if (patch === null) return list.filter((post) => post.id !== id);
  return list.map((post) => (post.id === id ? patch(post) : post));
};

const jobPost: Module<any, {}> = {
  state: {
    jobPost: {
      data: null,
      isLoading: false,
    },
    jobPosts: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    jobPost: (state: { jobPost: any }) => state.jobPost,
    jobPosts: (state: { jobPosts: any }) => state.jobPosts,
  },
  mutations: {
    [SET_JOB_POST]: (
      state: { jobPost: StateObject },
      payload: { data?: any; status?: any; isLoading: any }
    ) => {
      if (payload.data !== undefined) state.jobPost.data = payload.data;
      state.jobPost.isLoading = payload.isLoading;
    },
    [SET_JOB_POSTS]: (
      state: { jobPosts: StateObject },
      payload: { data?: any; status?: any; isLoading: any }
    ) => {
      if (payload.data !== undefined) state.jobPosts.data = payload.data;
      state.jobPosts.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_JOB_POST]: async ({ commit }: any, payload: RegisterJobPost) => {
      commit(SET_JOB_POST, { data: null, isLoading: true });

      const result: AxiosResponse = await JobPost.registerJobPost(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_JOB_POST, { data: result.data.data, isLoading: false });
          return result.data.data;
        }
        commit(SET_JOB_POST, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_JOB_POST, { isLoading: false });
      return false;
    },
    [GET_JOB_POSTS]: async ({ commit }: any, payload: GetJobPost) => {
      commit(SET_JOB_POSTS, { isLoading: true });

      const result: AxiosResponse = await JobPost.getJobPosts(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_JOB_POSTS, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_JOB_POSTS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_JOB_POSTS, { isLoading: false });
      return false;
    },
    [GET_JOB_POST]: async ({ commit }: any, payload: GetJobPost) => {
      commit(SET_JOB_POST, { data: null, isLoading: true });

      const result: AxiosResponse = await JobPost.getJobPosts(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_JOB_POST, { data: result.data.data[0] ?? null, isLoading: false });
          return true;
        }
        commit(SET_JOB_POST, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_JOB_POST, { isLoading: false });
      return false;
    },
    [EDIT_JOB_POST]: async ({ commit, state }: any, payload: EditJobPost) => {
      commit(SET_JOB_POST, { isLoading: true });

      const result: AxiosResponse = await JobPost.editJobPost(payload);

      if (result) {
        if (result.data.status) {
          const updated = result.data.data;
          commit(SET_JOB_POST, { data: updated, isLoading: false });
          commit(SET_JOB_POSTS, {
            data: patchList(state.jobPosts.data, updated.id, (post) => ({ ...post, ...updated })),
            isLoading: state.jobPosts.isLoading,
          });
          return updated;
        }
        commit(SET_JOB_POST, { isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_JOB_POST, { isLoading: false });
      return false;
    },
    [TOGGLE_JOB_POST_IS_ACTIVE]: async ({ commit, state }: any, payload: { id: string }) => {
      const result: AxiosResponse = await JobPost.toggleJobPostIsActive(payload);

      if (result) {
        if (result.data.status) {
          const updated = result.data.data;
          commit(SET_JOB_POSTS, {
            data: patchList(state.jobPosts.data, updated.id, (post) => ({ ...post, isActive: updated.isActive })),
            isLoading: state.jobPosts.isLoading,
          });
          if (state.jobPost.data && state.jobPost.data.id === updated.id) {
            commit(SET_JOB_POST, { data: { ...state.jobPost.data, isActive: updated.isActive }, isLoading: false });
          }
          return updated;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      return false;
    },
    [DELETE_JOB_POST]: async ({ commit, state }: any, payload: string) => {
      commit(SET_JOB_POST, { isLoading: true });

      const result: AxiosResponse = await JobPost.deleteJobPost(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_JOB_POST, { data: result.data.data, isLoading: false });
          commit(SET_JOB_POSTS, {
            data: patchList(state.jobPosts.data, payload, null),
            isLoading: state.jobPosts.isLoading,
          });
          return true;
        }
        commit(SET_JOB_POST, { isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_JOB_POST, { isLoading: false });
      return false;
    },
    [CLEAR_JOB_POST_ISLOADING]: ({ commit }) => {
      commit(SET_JOB_POST, { isLoading: false });
      commit(SET_JOB_POSTS, { isLoading: false });
    },
  },
};

export default jobPost;
